import React, { useEffect, useState } from "react";
import BackHeader from "@components/header/BackHeader";
import LabelWithInput from "@components/input/LabelWithInput";
import BasicFormInput from "@components/input/BasicFormInput";
import BasicButton from "@components/button/BasicButton";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import BankSelector from "@pages/myPage/_component/BankSelector";
import BasicModal from "@components/modal/BasicModal";
import useAxios from "@libs/axios/useAxios";
import { MEMBER_URL } from "@libs/apiUrl";
import { Flex } from "antd";
import { zodResolver } from "@hookform/resolvers/zod";
import { bankAccountSchema } from "@libs/zod/zodVaildation";
import useUserInfoStore from "@store/useUserInfoStore";
import { useNavigate } from "react-router-dom";

const Container = styled.form({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "calc(100vh - 5.6rem)",
  gap: "4rem",
  padding: "3rem 2rem 4.2rem",
});
const ContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "4rem",
});

const Title = styled.h2({
  fontSize: "2rem",
  fontWeight: 700,
});

const Description = styled.div({
  display: "flex",
  flexDirection: "column",
  lineHeight: "2rem",
  color: "#808080",
  width: "100%",
  alignItems: "center",
});

const SuccessModalContent = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4.4rem",
  width: "100%",
  ".title": {
    fontSize: "1.6rem",
    textAlign: "center",
    fontWeight: theme.fontWeight.medium,
  },
}));

const DescBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.2rem",
  backgroundColor: theme.color.border_gray,
  border: `1px solid #e0e0e0`,
  width: "100%",
  padding: "1.2rem",
  lineHeight: "1.6rem",
  fontSize: "1.2rem",

  ".descTitle": {
    color: theme.color.orange,
    fontWeight: 700,
  },
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
  marginTop: "0.6rem",
  height: "1.5rem",
}));

const BankAccountManagement = () => {
  const [error, setError] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const { axiosInstance } = useAxios();
  const { userInfo } = useUserInfoStore();
  const navigate = useNavigate();

  const method = useForm({
    resolver: zodResolver(bankAccountSchema),
    mode: "onChange",
    reValidateMode: "onChange",

    defaultValues: {
      account_holder: "",
      bank_cd: "",
      account_number: "",
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = method;

  const onSubmit = async (data: any) => {
    if (
      data.account_holder === "" ||
      data.account_number === "" ||
      data.bank_cd === ""
    ) {
      setError(true);
      return;
    }
    const res = await axiosInstance.patch(MEMBER_URL, {
      account_holder: data.account_holder,
      bank_cd: data.bank_cd,
      account_number: data.account_number,
    });
    if (res.status === 200) {
      setSuccessModal(true);
    }
  };

  const handleKakaoChat = () => {
    const url = "http://pf.kakao.com/_vfRpxj";
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (userInfo) {
      setValue("account_holder", userInfo.account_holder || "");
      setValue("bank_cd", userInfo.bank_cd || "");
      setValue("account_number", userInfo.account_number || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const handleChange = (e: any) => {
    const value = e.target.value;
    const filteredValue = value.replace(
      /[!~@#$%^&*()_+0-9\s-{}[\]|:<>?\\/;\\'",.=`₩]/g,
      "",
    );

    e.target.value = filteredValue;
  };

  return (
    <FormProvider {...method}>
      <BackHeader title="계좌관리" />
      <Container onSubmit={handleSubmit(onSubmit)}>
        <Title>{"정산 받을 거래 계좌를 알려주세요"}</Title>
        <ContentWrapper>
          <LabelWithInput label="예금주" required>
            <BasicFormInput
              name="account_holder"
              placeholder={"예금주를 입력해주세요."}
              // onlyText
              type="text"
              onInput={handleChange}
              fontSize="1.6rem"
              max={100}
            />
          </LabelWithInput>

          <LabelWithInput label={"은행명"} required>
            <BankSelector name="bank_cd" placeholder={"은행 선택"} />
          </LabelWithInput>

          <LabelWithInput label={"계좌번호 변경"} required>
            <BasicFormInput
              name="account_number"
              type="text"
              placeholder={"계좌번호를 입력해주세요."}
              fontSize="1.6rem"
            />
            {errors.account_number && (
              <ErrorMessage>{errors.account_number?.message}</ErrorMessage>
            )}
          </LabelWithInput>
        </ContentWrapper>

        <Description>
          <span>* {"계좌 입력 사항에 대한 책임은 온전히"}</span>
          <span>{"사용자에게 있습니다. 아래 버튼을 누르시면"}</span>
          <span>{"해당 사항에 동의하는 걸로 간주됩니다."}</span>
        </Description>

        <BasicButton type="submit" title="등록" />
      </Container>

      <BasicModal notice open={error} setOpen={setError}>
        <div
          style={{
            padding: "4.4rem 2rem",
          }}>
          필수값을 입력해주세요.
        </div>
      </BasicModal>

      <BasicModal
        open={successModal}
        setOpen={setSuccessModal}
        activeName="인증하러가기"
        activeEvent={handleKakaoChat}
        closeEvent={() => navigate("/my-page?tab=2")}
        closeName="확인"
        closeBorder>
        <SuccessModalContent>
          <span className="title">{"계좌가 등록되었습니다."}</span>

          <DescBox>
            <span className="descTitle">* {"사업자 인증 안내"}</span>
            <Flex vertical>
              <span>
                {
                  "사업자 회원은 부가세 세금계산서 발행 대상이므로 반드시 사업자 인증을 요청을 완료하셔야 합니다."
                }
              </span>
              <span>
                {
                  "사업자 미인증 후 판매 시 제제 및 법적 이슈가 발생할 수 있습니다."
                }
              </span>
            </Flex>
          </DescBox>
        </SuccessModalContent>
      </BasicModal>
    </FormProvider>
  );
};

export default BankAccountManagement;
